import React from 'react';
import { date, mixed, number, object, string } from 'yup';

import { useToast } from 'Component/Alerta';
import Form, { Arquivo, Botao, Campo, Selecao } from 'Component/Form';
import Icone from 'Component/Icone';
import Imagem from 'Component/Imagem';
import Usuario from 'Interface/Usuarios';
import { editarUsuario as editarUsuarioAction } from 'Reducer/Autenticacao';
import { useEditarPerfilMutation, useRecuperarPerfilQuery } from 'Service';
import { useAppDispatch } from 'Store';
import { ESTADOS, IMAGENS, MASCARA_CPF, MASCARA_TELEFONE } from 'View/constants';

const regras = object({
    nome: string().required('Obrigatório'),
    email: string().email('Inválido').required('Obrigatório'),
    rg: string().nullable().optional(),
    orgao_emissor: string().nullable().optional(),
    estado_orgao_emissor: string().nullable().optional(),
    telefone1: string().required('Obrigatório'),
    telefone2: string().nullable().optional(),
    nascimento: date().nullable().optional(),
    cep: string().nullable().optional(),
    estado: string().nullable().optional(),
    cidade: string().nullable().optional(),
    bairro: string().nullable().optional(),
    endereco: string().nullable().optional(),
    numero: number().nullable().integer().positive('Inválido').optional(),
    complemento: string().nullable().optional(),
    imagem: mixed()
        .test('tamanho', 'O arquivo é muito grande', valor => {
            if (valor && valor.size) {
                return valor.size <= 500000;
            }
            return true;
        })
        .test('tipo', 'Formato não suportado', valor => {
            if (valor && valor.type) {
                return IMAGENS.includes(valor.type)
            }
            return true;
        })
        .optional(),
});

const Perfil = (): React.ReactElement =>
{
    const toast = useToast();
    const dispatch = useAppDispatch();
    const [ editarUsuario ] = useEditarPerfilMutation();

    const { data: { usuario } = {} } = useRecuperarPerfilQuery();

    const enviar = async (dados: Usuario): Promise<void> =>
    {
        try {
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            const { cpf, email, ...usuario } = dados;

            const resposta = await editarUsuario(usuario).unwrap();

            dispatch(editarUsuarioAction({ usuario: resposta.usuario }));

            toast({ conteudo: 'Perfil alterado com sucesso!', tema: 'success' });
        } catch (error) {
            console.log(error);
            toast({
                titulo: 'Erro ao alterar senha',
                conteudo: 'Verifique os logs para maiores detalhes.',
                tema: 'danger',
            });
        }
    }

    return (
        <div className="animate__animated animate__fadeIn">
            <div className="container px-4 mt-5">
                <p className="display-1 text-center text-white">
                    Meu perfil
                </p>
                <p className="lead text-muted text-center">
                    Atualize seus dados cadastrais e mantenha as informações em dia!
                </p>
            </div>
            <div className="container-fluid mb-5">
                <Form onSubmit={ enviar } regras={ regras } inicial={ usuario }>
                    <div className="row justify-content-center mt-5 px-3">
                        <div className="col-md-4">
                            <div className="bg-white shadow-sm rounded p-4 mt-5">
                                <div className="mb-5 d-flex justify-content-center mt-n4">
                                    <Imagem
                                        src={ usuario?.imagem }
                                        alt={ usuario?.nome }
                                        className="rounded-circle shadow-lg mt-n5 object-fit-cover"
                                        width="100"
                                        height="100"
                                    />
                                </div>
                                <Arquivo name="imagem" />
                                <div className="row">
                                    <div className="col-md-12">
                                        <Campo name="nome" rotulo="Nome" />
                                    </div>
                                    <div className="col-md-12">
                                        <Campo name="email" rotulo="E-mail" prefixar={<Icone nome="envelope-fill" />} desativado />
                                    </div>
                                    <div className="col-md-6">
                                        <Campo name="cpf" rotulo="CPF" mascara={ MASCARA_CPF } desativado />
                                    </div>
                                    <div className="col-md-6">
                                        <Campo name="rg" rotulo="RG" />
                                    </div>
                                    <div className="col-md-6">
                                        <Campo name="orgao_emissor" rotulo="Orgão emissor" />
                                    </div>
                                    <div className="col-md-6">
                                        <Campo name="estado_orgao_emissor" rotulo="Estado do orgão emissor" />
                                    </div>
                                    <div className="col-md-6">
                                        <Campo name="telefone1" rotulo="Telefone 1" mascara={ MASCARA_TELEFONE } prefixar={<Icone nome="telephone-fill" />} />
                                    </div>
                                    <div className="col-md-6">
                                        <Campo name="telefone2" rotulo="Telefone 2" mascara={ MASCARA_TELEFONE } prefixar={<Icone nome="telephone-fill" />} />
                                    </div>
                                    <div className="col-md-6">
                                        <Campo name="nascimento" type="date" rotulo="Nascimento" prefixar={<Icone nome="calendar-fill" />} />
                                    </div>
                                    <div className="col-md-6">
                                        <Campo name="cep" rotulo="CEP"  prefixar={<Icone nome="geo-fill" />} />
                                    </div>
                                    <div className="col-md-5">
                                        <Selecao name="estado" rotulo="Estado onde reside" opcoes={ ESTADOS } />
                                    </div>
                                    <div className="col-md-7">
                                        <Campo name="cidade" rotulo="Cidade onde reside" />
                                    </div>
                                    <div className="col-md-12">
                                        <Campo name="bairro" rotulo="Bairro onde reside" />
                                    </div>
                                    <div className="col-md-8">
                                        <Campo name="endereco" rotulo="Endereço de onde reside" />
                                    </div>
                                    <div className="col-md-4">
                                        <Campo name="numero" type="number" rotulo="Número" />
                                    </div>
                                    <div className="col-md-12">
                                        <Campo name="complemento" rotulo="Complemento" semMargem />
                                    </div>
                                </div>

                                <div className="mt-5">
                                    <Botao>Atualizar meu perfil</Botao>
                                </div>
                            </div>
                        </div>
                    </div>
                </Form>
            </div>
        </div>
    )
}

export default Perfil;
